// Ported from microsites/womens-conference/src/components/TicketCard.tsx.
// The stub is verbatim. The receipt is built from what the event payload
// carries: her registration's options (no per-line prices there) and the
// invoice's total and payments, with the others on it by face, no amounts.
import type { ConferenceConfig } from '../lib/conference';
import { art } from '../lib/art';
import { Avatar } from './Avatar';
import { Sprig } from './Botanical';
import { money } from '../lib/format';

export interface TicketLine {
  key: string;
  label: string;
}

export interface PartyMember {
  eventParticipantId: number;
  name: string;
  imageUrl: string | null;
  isMe: boolean;
}

/**
 * HER TICKET, AND THE RECEIPT UNDER IT
 * ====================================
 *
 * The ticket is now the cloakroom stub the welcome curtain flies across the
 * screen — the one thing on this site people actually reacted to. Same parts:
 * the hero's watercolor ground, a double rule inset from the edge, the year set
 * vertically up both margins, a botanical mark above and below, and the display
 * face the speakers' names are set in. Perforated edges via `.ticket-edges`, so
 * it reads as something torn from a book rather than a card with rounded
 * corners.
 *
 * ONE TICKET, ALWAYS HERS. Even when she paid for four people, the stub is her
 * own, because a ticket is the thing you hold at a door.
 *
 * THERE IS NO CHECK-IN, AND SO THERE IS NO CODE. This carried a QR labelled a
 * preview, on the assumption that scanning arrives later. It does not: women
 * walk into this conference. A code nobody will ever scan is a promise the
 * event will not keep, and it took the visual weight of the real content while
 * making the card look like a boarding pass. The stub is a keepsake and a
 * confirmation, which is the whole job.
 *
 * THE RECEIPT IS THE PART THAT KNOWS ABOUT EVERYONE. About a quarter of What
 * Matters registrants sit on somebody else's invoice, and until now each of them
 * was shown a ticket as though she had come alone. So the money moves under the
 * stub and grows a party list — but only for the woman who checked out, because
 * `Purchaser_Contact_ID` is what "the person who did the registration" means and
 * the list contains other people's names and what was spent on them. Everyone
 * else sees their own stub and their own lines, which is all their business.
 */
export function TicketCard({
  config,
  name,
  lines,
  total,
  paid,
  party,
  stamp,
}: {
  config: ConferenceConfig;
  name: string;
  lines: TicketLine[];
  /** The invoice's total and what has been paid toward it. */
  total: number;
  paid: number;
  /** Everyone on the invoice. */
  party?: PartyMember[];
  /** Struck across the stub while the place is on its way to someone else.
   *  `land` plays the stamp coming down, for the moment it is sent. */
  stamp?: { text: string; land?: boolean } | null;
}) {
  const YEAR = config.eventStarts.slice(0, 4);
  const others = party?.filter((m) => !m.isMe) ?? [];

  return (
    <div className="flex flex-col gap-3">
      {/* The stub. Fixed aspect so the ornament spacing holds at any width, and
          container units so the type scales with it rather than with the page. */}
      <div className="wm-ticket-edges wm-ticket-punched bg-wm-sage-950 [container-type:size] relative aspect-[16/7] overflow-hidden">
        <img
          src={art('/art/watercolor.png')}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div aria-hidden className="bg-wm-sage-950/55 absolute inset-0" />

        <div className="border-wm-cream/35 absolute inset-[4%] rounded-[10px] border" />
        <div className="border-wm-cream/15 absolute inset-[6.5%] rounded-[7px] border" />

        <p className="wm-kicker text-wm-cream absolute top-1/2 left-[3%] -translate-y-1/2 rotate-180 text-[clamp(0.34rem,1.7cqw,0.6rem)] opacity-40 [writing-mode:vertical-rl]">
          {YEAR}
        </p>
        <p className="wm-kicker text-wm-cream absolute top-1/2 right-[3%] -translate-y-1/2 text-[clamp(0.34rem,1.7cqw,0.6rem)] opacity-40 [writing-mode:vertical-rl]">
          {YEAR}
        </p>

        <div className="absolute inset-0 flex flex-col items-center justify-center px-[10%] text-center">
          <span className="text-wm-cream opacity-45">
            <Sprig kind="bloom" className="h-[3.6cqh] min-h-3 w-[3.6cqh] min-w-3" />
          </span>
          <p className="wm-script text-wm-blush mt-[1cqh] text-[clamp(0.7rem,4.4cqh,1.1rem)] leading-none">
            {config.ministryMark}
          </p>
          <p className="wm-display text-wm-cream text-[clamp(1.3rem,10cqh,2.6rem)] leading-tight tracking-wide">
            {config.name}
          </p>
          <span className="text-wm-cream opacity-45">
            <Sprig kind="bloom" className="h-[3.6cqh] min-h-3 w-[3.6cqh] min-w-3" />
          </span>
          <p className="wm-kicker text-wm-cream mt-[3cqh] text-[clamp(0.34rem,1.9cqw,0.62rem)] opacity-80">
            {name}
          </p>
        </div>

        {stamp ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <span
              className={`wm-stamp border-wm-marigold text-wm-marigold bg-wm-sage-950 rounded-md border-2 px-[4cqw] py-[1.5cqh] text-[clamp(0.7rem,5cqh,1.15rem)] font-bold tracking-[0.2em] uppercase ${
                stamp.land ? 'wm-stamp-land' : ''
              }`}
            >
              {stamp.text}
            </span>
          </div>
        ) : null}
      </div>

      {/* The receipt. Ruled paper under the stub, the way a booking confirmation
          sits under the ticket it came with. */}
      {/* No invoice (the time machine's demo attendee): no receipt. */}
      {lines.length || total ? (
        <div className="wm-card p-5">
          <div>
            <p className="wm-kicker">{others.length ? 'Receipt' : 'Your order'}</p>
            {others.length ? (
              <p className="text-wm-sage-500 mt-0.5 text-xs">
                You registered {party!.length} {party!.length === 1 ? 'person' : 'people'}
              </p>
            ) : null}
          </div>

          <ul className="mt-4 flex flex-col gap-1 text-sm">
            {lines.map((line) => (
              <li key={line.key} className="text-wm-sage-700 truncate">
                {line.label}
              </li>
            ))}
            <li className="border-wm-sage-200 mt-1 flex justify-between border-t pt-2 font-semibold">
              <span>{others.length ? 'Paid in total' : 'Total'}</span>
              <span className="tabular-nums">
                {paid >= total ? money(total) : `${money(paid)} of ${money(total)}`}
              </span>
            </li>
          </ul>

          {others.length ? (
            <div className="border-wm-sage-200 mt-4 border-t pt-4">
              <p className="wm-kicker">Also on this registration</p>
              <ul className="mt-2 flex flex-col gap-2">
                {others.map((member) => (
                  <li key={member.eventParticipantId} className="flex items-center gap-3">
                    <Avatar name={member.name} imageSrc={member.imageUrl} size={32} />
                    <span className="min-w-0 grow truncate text-sm">{member.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
